// Prezentacyjna warstwa nad `user_status`/`reason_code` zwracanymi przez API
// modułu kompatybilności (KONTRAKT §2, §4 + 00_ANEKS_v1.1.md A1). Front NIE
// wnioskuje kompatybilności — to tylko mapowanie gotowych wartości enum na
// etykiety, kolory i teksty.
import {
  faCircleCheck, faTriangleExclamation, faCircleXmark, faCodeCompare, faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';
import i18n from '../i18n';

// Kolejność krytyczna dla UI: "unknown" MUSI wyglądać neutralnie, nigdy jak "incompatible" —
// i musi być odróżnialne od "incompatible" (inny kolor, inna ikona, inny tekst), bo właśnie
// to rozróżnienie ("nie zadziała" vs. "nie wiemy") jest sensem tego modułu (ANEKS A3).
export const STATUS_META = {
  compatible: {
    label: 'Kompatybilne',
    icon: faCircleCheck,
    tone: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    iconTone: 'text-emerald-600',
    badgeTone: 'bg-emerald-600 text-white',
  },
  conditional: {
    label: 'Kompatybilne warunkowo',
    icon: faTriangleExclamation,
    tone: 'bg-amber-50 border-amber-200 text-amber-800',
    iconTone: 'text-amber-600',
    badgeTone: 'bg-amber-500 text-white',
  },
  incompatible: {
    label: 'Niekompatybilne',
    icon: faCircleXmark,
    tone: 'bg-rose-50 border-rose-200 text-rose-700',
    iconTone: 'text-rose-600',
    badgeTone: 'bg-rose-600 text-white',
  },
  conflicting: {
    label: 'Sprzeczne informacje',
    icon: faCodeCompare,
    tone: 'bg-orange-50 border-orange-200 text-orange-800',
    iconTone: 'text-orange-600',
    badgeTone: 'bg-orange-500 text-white',
  },
  // Celowo szare/neutralne, bez czerwieni czy krzyżyków — brak danych to nie odmowa.
  unknown: {
    label: 'Brak potwierdzonych danych',
    icon: faCircleQuestion,
    tone: 'bg-gray-50 border-gray-200 text-gray-600',
    iconTone: 'text-gray-400',
    badgeTone: 'bg-gray-400 text-white',
  },
};

// Etykieta z bieżącego słownika (status.*); polskie `label` w STATUS_META zostaje dla
// miejsc, które czytają obiekt bezpośrednio.
export const getStatusMeta = (userStatus) => {
  const klucz = STATUS_META[userStatus] ? userStatus : 'unknown';
  return { ...STATUS_META[klucz], label: i18n.t(`zgodnosc:status.${klucz}`) };
};

// Domyślne komunikaty, gdy reason_code jest puste ALBO nierozpoznane (enum rośnie
// wraz z nowymi dokumentami producentów — nieznana wartość nie może wywalić widoku
// ani pokazać surowego kodu, ma spaść na ten ogólny tekst).
export const DEFAULT_STATUS_TEXT = {
  unknown: 'Nie znaleźliśmy oficjalnego potwierdzenia dla tej kombinacji. To nie oznacza, że urządzenia nie zadziałają — oznacza, że producent nie opublikował danych.',
  incompatible: 'Te urządzenia nie współpracują ze sobą.',
};

// Zdanie doklejane WYŁĄCZNIE przy "unknown" — przy "incompatible" odpowiedź jest
// jednoznaczna i taka zachęta byłaby myląca (ANEKS A1).
export const VERIFY_ENCOURAGEMENT = 'Przed zakupem potwierdź konfigurację u producenta lub instalatora.';

// Pełny słownik z ANEKS A1 — 8 wartości. `reason_code` bywa wypełnione też dla
// "incompatible" (kroki 2/4/5 algorytmu), nie tylko dla "unknown".
export const REASON_CODE_TEXT = {
  unmapped_product: 'Nie rozpoznaliśmy modelu jednego z urządzeń. Wybierz inny wariant produktu.',
  no_rule_in_database: 'Nie mamy jeszcze zweryfikowanych danych dla tej kombinacji.',
  not_listed_in_source: 'Tego magazynu nie ma na oficjalnej liście producenta falownika. To nie oznacza, że nie zadziała — producent po prostu go nie przetestował.',
  insufficient_evidence: 'Dostępne informacje pochodzą wyłącznie ze źródeł handlowych i nie wystarczają do potwierdzenia.',
  documentation_unreadable: 'Producent falownika nie opublikował czytelnej listy kompatybilnych magazynów dla tej serii.',
  closed_ecosystem: 'Ten magazyn działa wyłącznie z falownikami tego samego producenta.',
  inverter_has_no_dc_battery_port: 'Ten falownik nie ma wejścia bateryjnego DC.',
  voltage_class_mismatch: 'Niezgodne klasy napięciowe — magazyn niskonapięciowy nie współpracuje z falownikiem wysokonapięciowym.',
};

// Zwraca null dla braku/nierozpoznanego kodu — wołający ma wtedy pokazać
// DEFAULT_STATUS_TEXT[userStatus] zamiast tego.
export const reasonCodeText = (reasonCode) => (reasonCode && REASON_CODE_TEXT[reasonCode]
  ? i18n.t(`zgodnosc:powod.${reasonCode}`) : null);

// Gotowy, kompletny tekst dla nagłówka karty wyniku: konkretny powód (jeśli znany)
// albo ogólny domyślny dla statusu, plus zachęta do weryfikacji tylko przy "unknown".
export const statusExplanation = (userStatus, reasonCode) => {
  const specific = reasonCodeText(reasonCode);
  const base = specific || (DEFAULT_STATUS_TEXT[userStatus] ? i18n.t(`zgodnosc:status_opis.${userStatus}`) : '');
  if (userStatus === 'unknown') return `${base} ${i18n.t('zgodnosc:zacheta_weryfikacji')}`;
  return base;
};
